import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { poolApi } from '@/api/calculators';
import type { PoolCalculateResponse, PoolInput, PoolModelEnvelopeResult, PoolPlacement, PoolApplication, PoolClimate, PoolUnit } from '@/api/calculators/types';
import { readStoredSession, writeStoredSession } from '../MeetingModeLauncher';

type Tab = 'forward' | 'reverse';

export function usePoolCalculator() {
  const navigate = useNavigate();
  const [session, setSession] = useState(() => readStoredSession());
  const [tab, setTab] = useState<Tab>('forward');

  // Forward-flow input state — pre-filled to the reference's 42×12×4 ft
  // Delhi composite Hotel case (no winter ops, 36hr target, 27°C target).
  const [climate, setClimate] = useState<PoolClimate>('composite');
  const [unit, setUnit] = useState<PoolUnit>('ft');
  const [inputMode, setInputMode] = useState<'dimensions' | 'volume'>('dimensions');
  const [volume, setVolume] = useState('');
  const [volumeUnit, setVolumeUnit] = useState<'L' | 'm3'>('m3');
  const [calculatedInput, setCalculatedInput] = useState<PoolInput | null>(null);
  function changeVolumeUnit(next: 'L' | 'm3') {
    const value = Number(volume.replace(/,/g, ''));
    if (volume.trim() && Number.isFinite(value) && next !== volumeUnit) {
      setVolume(String(Number((value * (next === 'L' ? 1000 : 0.001)).toPrecision(12))));
    }
    setVolumeUnit(next);
  }

  const [length, setLength] = useState<number | ''>(42);
  const [width, setWidth] = useState<number | ''>(12);
  const [depthAvg, setDepthAvg] = useState<number | ''>(4);
  const [placement, setPlacement] = useState<PoolPlacement>('outdoor');
  const [application, setApplication] = useState<PoolApplication>('hotel');
  const [winterOp, setWinterOp] = useState<'yes' | 'no'>('no');
  const [heatTimeTarget, setHeatTimeTarget] = useState<24 | 36 | 48>(36);
  const [targetTemp, setTargetTemp] = useState<26 | 27 | 28 | 29 | 30>(27);

  // Reverse-flow input state
  const [revModel, setRevModel] = useState<string>('ST-HP-SP-27');
  const [revClimate, setRevClimate] = useState<PoolClimate>('composite');
  const [revPlacement, setRevPlacement] = useState<PoolPlacement>('outdoor');
  const [revWinter, setRevWinter] = useState<'yes' | 'no'>('no');
  const [revQty, setRevQty] = useState<number>(1);
  const [revLength, setRevLength] = useState<number | ''>('');
  const [revWidth, setRevWidth] = useState<number | ''>('');
  const [revDepth, setRevDepth] = useState<number | ''>('');
  const [revUnit, setRevUnit] = useState<PoolUnit>('ft');

  // Result state
  const [forwardResult, setForwardResult] =
    useState<PoolCalculateResponse | null>(null);
  const [reverseResult, setReverseResult] =
    useState<PoolModelEnvelopeResult | null>(null);
  const [forwardSubTab, setForwardSubTab] = useState<'rec' | 'roi'>('rec');

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Lead capture + save quotation
  const [leadName, setLeadName] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadCity, setLeadCity] = useState('');
  const [leadNotes, setLeadNotes] = useState('');
  const [savedQuotationId, setSavedQuotationId] = useState<string | null>(null);
  const [savingQuote, setSavingQuote] = useState(false);
  const [sendingLead, setSendingLead] = useState(false);
  const [leadSent, setLeadSent] = useState(false);

  // Narrow session for closures
  const sess = session!;

  async function refreshSession() {
    const fresh = await poolApi.startSession(sess.leadId);
    const updated = {
      token: fresh.token,
      sessionId: fresh.sessionId,
      expiresAt: fresh.expiresAt,
      leadId: sess.leadId,
      leadLabel: sess.leadLabel,
    };
    writeStoredSession(updated);
    setSession(updated);
    return updated;
  }

  function buildForwardInput(): PoolInput {
    if (inputMode === 'volume') {
      const amount = Number(volume.replace(/,/g, ''));
      if (!Number.isFinite(amount) || amount <= 0) throw new RangeError('Please enter a positive pool volume.');
      return { input_mode: 'volume', volume: amount, volume_unit: volumeUnit,
        climate_zone: climate, placement, application, winter_operation: winterOp === 'yes',
        heat_time_target: heatTimeTarget, target_temp: targetTemp };
    }

    if (
      typeof length !== 'number' ||
      typeof width !== 'number' ||
      typeof depthAvg !== 'number' ||
      length <= 0 ||
      width <= 0 ||
      depthAvg <= 0
    ) {
      throw new RangeError('Please enter pool dimensions.');
    }
    return {
      climate_zone: climate,
      length,
      width,
      depth_avg: depthAvg,
      unit,
      placement,
      application,
      winter_operation: winterOp === 'yes',
      heat_time_target: heatTimeTarget,
      target_temp: targetTemp,
    };
  }

  async function handleCalculateForward() {
    setError(null);
    setLoading(true);
    setSavedQuotationId(null);
    setLeadSent(false);
    try {
      const input = buildForwardInput();
      let res: PoolCalculateResponse;
      try {
        res = await poolApi.calculate(input, sess.token);
      } catch (e: any) {
        if (e?.response?.status === 403) {
          const fresh = await refreshSession();
          res = await poolApi.calculate(input, fresh.token);
        } else {
          throw e;
        }
      }
      setForwardResult(res);
      setCalculatedInput(input);
      setForwardSubTab('rec');
      window.setTimeout(() => {
        document
          .getElementById('pool-results')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    } catch (e: any) {
      setError(
        e?.response?.data?.details?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Calculation failed. Please review the inputs.',
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleCalculateReverse() {
    setError(null);
    setLoading(true);
    try {
      const body: Parameters<typeof poolApi.modelEnvelope>[0] = {
        model_id: revModel,
        qty_working: revQty,
        climate_zone: revClimate,
        placement: revPlacement,
        winter_operation: revWinter === 'yes',
      };
      if (
        typeof revLength === 'number' &&
        typeof revWidth === 'number' &&
        typeof revDepth === 'number' &&
        revLength > 0 &&
        revWidth > 0 &&
        revDepth > 0
      ) {
        body.pool_inputs = {
          length: revLength,
          width: revWidth,
          depth_avg: revDepth,
          unit: revUnit,
        };
      }
      let res: PoolModelEnvelopeResult;
      try {
        res = await poolApi.modelEnvelope(body, sess.token);
      } catch (e: any) {
        if (e?.response?.status === 403) {
          const fresh = await refreshSession();
          res = await poolApi.modelEnvelope(body, fresh.token);
        } else {
          throw e;
        }
      }
      setReverseResult(res);
      window.setTimeout(() => {
        document
          .getElementById('pool-reverse-result')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    } catch (e: any) {
      setError(
        e?.response?.data?.details?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Could not fetch model envelope.',
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveQuotation(): Promise<string | null> {
    if (!forwardResult || !calculatedInput) return null;
    setSavingQuote(true);
    setError(null);
    try {
      const out = await poolApi.saveQuotation(calculatedInput, forwardResult, sess.token);
      setSavedQuotationId(out.id);
      return out.id;
    } catch (e: any) {
      setError(
        e?.response?.data?.details?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Could not save quotation.',
      );
      return null;
    } finally {
      setSavingQuote(false);
    }
  }

  async function handleSendLead() {
    if (!leadName.trim() || !leadPhone.trim()) {
      setError('Please enter your name and mobile number so the Sunstellar team can reach you.');
      return;
    }
    setSendingLead(true);
    setError(null);
    try {
      if (!savedQuotationId && forwardResult) await handleSaveQuotation();
      setLeadSent(true);
    } catch (e: any) {
      setError(
        e?.response?.data?.details?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Could not send enquiry. Please retry.',
      );
    } finally {
      setSendingLead(false);
    }
  }

  function handlePrint() {
    if (!forwardResult) {
      setError('Please calculate a recommendation first.');
      return;
    }
    window.print();
  }

  return { calculatedInput, inputMode, setInputMode, volume, setVolume, volumeUnit, changeVolumeUnit, navigate, session, setSession, tab, setTab, climate, setClimate, unit, setUnit, length, setLength, width, setWidth, depthAvg, setDepthAvg, placement, setPlacement, application, setApplication, winterOp, setWinterOp, heatTimeTarget, setHeatTimeTarget, targetTemp, setTargetTemp, revModel, setRevModel, revClimate, setRevClimate, revPlacement, setRevPlacement, revWinter, setRevWinter, revQty, setRevQty, revLength, setRevLength, revWidth, setRevWidth, revDepth, setRevDepth, revUnit, setRevUnit, forwardResult, setForwardResult, reverseResult, setReverseResult, forwardSubTab, setForwardSubTab, error, setError, loading, setLoading, leadName, setLeadName, leadPhone, setLeadPhone, leadEmail, setLeadEmail, leadCity, setLeadCity, leadNotes, setLeadNotes, savedQuotationId, setSavedQuotationId, savingQuote, setSavingQuote, sendingLead, setSendingLead, leadSent, setLeadSent, handleCalculateForward, handleCalculateReverse, handleSaveQuotation, handleSendLead, handlePrint };
}
